import type { UnitId } from "@bl/domain";
import { DISPLAY_UNITS, type Dimension, plural } from "@bl/domain";
import { Check, ChevronDown, ChevronRight, X } from "lucide-react";
import {
  type ComponentProps,
  type CSSProperties,
  cloneElement,
  isValidElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { categoryLabel, unitLabel } from "../format";
import { useLayout } from "../layout/LayoutProvider";

/* ---------- Page title ---------- */

/**
 * The browser tab's title: the page's own name, then the app's, so tabs, history and a screen
 * reader's page change each say where they are (the re-critique, 2026-10-06: every page said
 * "Inventory"). Left out parts are skipped, so a page can pass a name still loading.
 */
export function usePageTitle(...parts: (string | null | undefined)[]) {
  const title = [...parts.filter(Boolean), "BreweryLogistics"].join(" · ");
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/* ---------- Toolbar ---------- */

/** A page with nothing to put in its toolbar shows none, rather than an empty bar (Tyler, 2026-10-01). */
export function Toolbar({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  if (!left && !right) return null;
  return (
    <div className="toolbar">
      <div className="toolbar-section">{left}</div>
      <div className="toolbar-section">{right}</div>
    </div>
  );
}

export function ToolbarButton({
  icon,
  label,
  badge,
  labeled = false,
  ...props
}: {
  icon: ReactNode;
  label: string;
  badge?: boolean;
  /** Keeps its word on a phone, where the toolbar shows only icons: the floor's own verbs. */
  labeled?: boolean;
} & ComponentProps<"button">) {
  return (
    <button
      type="button"
      className={`toolbar-button ${labeled ? "labeled" : ""}`}
      title={label}
      {...props}
    >
      {icon}
      <span className="label">{label}</span>
      {badge ? <span className="badge" aria-hidden /> : null}
    </button>
  );
}

export const ToolbarSeparator = () => <div className="toolbar-separator" aria-hidden />;

/* ---------- Menu ---------- */

/**
 * Where an arrow key, Home or End moves to along a row or list of `items`, from the focused one;
 * null for any other key. `next` and `back` are the keys for each way.
 */
function stepTo(
  e: ReactKeyboardEvent,
  items: HTMLElement[],
  next: string,
  back: string,
): HTMLElement | null {
  const at = items.indexOf(document.activeElement as HTMLElement);
  const to =
    e.key === next
      ? (at + 1) % items.length
      : e.key === back
        ? (at - 1 + items.length) % items.length
        : e.key === "Home"
          ? 0
          : e.key === "End"
            ? items.length - 1
            : null;
  return to === null ? null : (items[to] ?? null);
}

/**
 * The keys a row of tabs answers to (the audit, 2026-10-06): the arrows, Home and End move along it
 * and choose the tab they land on. Each tab takes `tabIndex={selected ? 0 : -1}`, so Tab moves past
 * the row in one step.
 */
export function onTabKeys(e: ReactKeyboardEvent<HTMLElement>) {
  const tabs = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')];
  const to = stepTo(e, tabs, "ArrowRight", "ArrowLeft");
  if (!to) return;
  e.preventDefault();
  to.focus();
  to.click();
}

export function Menu({
  icon,
  label,
  badge,
  align = "right",
  labeled = false,
  trigger,
  children,
}: {
  icon?: ReactNode;
  /** The toolbar button's word, and the menu's name to a screen reader. */
  label: string;
  badge?: boolean;
  align?: "left" | "right";
  /** As a toolbar button's: keeps its word on a phone. */
  labeled?: boolean;
  /** A button of its own in place of the toolbar button, given what opens the menu. */
  trigger?: (opener: ComponentProps<"button">) => ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const items = () => [
    ...(ref.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)') ?? []),
  ];
  useEffect(() => {
    if (!open) return;
    // Opened, its first item takes the focus, and the arrows move through the rest.
    ref.current?.querySelector<HTMLElement>('[role^="menuitem"]:not(:disabled)')?.focus();
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      ref.current?.querySelector<HTMLElement>("[aria-haspopup]")?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const opener: ComponentProps<"button"> = {
    "aria-haspopup": "menu",
    "aria-expanded": open,
    "aria-controls": id,
    onClick: () => setOpen((o) => !o),
  };
  return (
    <div className={`menu ${labeled ? "labeled" : ""}`} ref={ref}>
      {trigger ? (
        trigger(opener)
      ) : (
        <ToolbarButton icon={icon} label={label} badge={badge} labeled={labeled} {...opener} />
      )}
      {open ? (
        <div
          className={`menu-panel ${align === "left" ? "align-left" : ""}`}
          id={id}
          role="menu"
          aria-label={label}
          onKeyDown={(e) => {
            if (e.key === "Tab") return setOpen(false);
            const to = stepTo(e, items(), "ArrowDown", "ArrowUp");
            if (!to) return;
            e.preventDefault();
            to.focus();
          }}
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}

export function MenuItem({
  checked,
  children,
  ...props
}: { checked?: boolean; children: ReactNode } & ComponentProps<"button">) {
  if (checked === undefined) {
    return (
      <button type="button" className="menu-item" role="menuitem" {...props}>
        <span>{children}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className="menu-item"
      role="menuitemcheckbox"
      aria-checked={checked}
      {...props}
    >
      <span className="check">{checked ? <Check size={16} /> : null}</span>
      <span>{children}</span>
    </button>
  );
}

export const MenuHeading = ({ children }: { children: ReactNode }) => (
  <div className="menu-heading">{children}</div>
);
export const MenuDivider = () => <div className="menu-divider" />;

/* ---------- Dialog ---------- */

/**
 * A modal on the native <dialog> element, which handles focus, Escape and the backdrop. Children render
 * only while open, so each opening starts a fresh form (and a fresh idempotency key). Children are a
 * <DialogForm>: a body that scrolls and a footer that stays put. A click on the backdrop closes it, as
 * Escape and the close button do (Tyler, 2026-10-01).
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // Where the press began: a drag that starts inside (selecting text in a field) and lets go on the
  // backdrop is not a click on the backdrop.
  const pressedOnBackdrop = useRef(false);
  // The contents mount once the dialog is open. Mounted with it, a field's autoFocus ran while the
  // dialog was still closed and went nowhere, and opening it focused Close instead: the Borrow
  // dialog's item search had to be clicked before it could be typed in.
  const [shown, setShown] = useState(false);
  useLayoutEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
    setShown(open);
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`dialog ${wide ? "wide" : ""}`}
      // Only its own: React passes a dialog's close and cancel up the component tree, so Add item
      // opened from inside the Borrow dialog would otherwise close the Borrow dialog with it.
      onClose={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onCancel={(e) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        onClose();
      }}
      // The dialog has no padding, so a press on the dialog itself, not its contents, is on the backdrop.
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedOnBackdrop.current) onClose();
        pressedOnBackdrop.current = false;
      }}
      onKeyDown={() => {}}
    >
      {open ? (
        <>
          <div className="dialog-header">
            <h2>{title}</h2>
            <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
          {shown ? children : null}
        </>
      ) : null}
    </dialog>
  );
}

export function DialogForm({
  onSubmit,
  footer,
  children,
}: {
  onSubmit: () => void;
  footer: ReactNode;
  children: ReactNode;
}) {
  return (
    <form
      className="dialog-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="dialog-body">{children}</div>
      <div className="dialog-footer">{footer}</div>
    </form>
  );
}

/* ---------- Fields ---------- */

export function Field({
  label,
  hint,
  required,
  className,
  children,
}: {
  label: string;
  hint?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  const hintId = useId();
  // The label points at the control by id rather than wrapping it, so a select's current option never
  // becomes part of its accessible name.
  const control = isValidElement<{ id?: string; "aria-describedby"?: string }>(children)
    ? cloneElement(children, { id, "aria-describedby": hint ? hintId : undefined })
    : children;
  return (
    <div className={`field ${className ?? ""}`}>
      <label htmlFor={id}>
        {label}
        {required ? <span className="required"> *</span> : null}
      </label>
      {control}
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export type QuantityUnit = UnitId | "pack";

/** A number and its unit. Offers the item's units, plus its pack ("bags") when it has one. */
export function QuantityInput({
  value,
  unit,
  onChange,
  dimension,
  packName,
  autoFocus,
  required,
  id,
  "aria-describedby": describedBy,
  unitName = "Unit",
}: {
  id?: string;
  "aria-describedby"?: string;
  value: string;
  unit: QuantityUnit;
  onChange: (value: string, unit: QuantityUnit) => void;
  dimension: Dimension;
  packName?: string | null;
  autoFocus?: boolean;
  required?: boolean;
  /** The unit select's name, where several stand together ("Unit for Crystal 60"). */
  unitName?: string;
}) {
  const units = DISPLAY_UNITS[dimension];
  return (
    <div className="quantity-input">
      <input
        id={id}
        aria-describedby={describedBy}
        className="input num"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        required={required}
        // biome-ignore lint/a11y/noAutofocus: dialogs open straight onto the amount
        autoFocus={autoFocus}
        pattern="[0-9]*[.]?[0-9]*"
        onChange={(e) => onChange(e.target.value.replace(",", "."), unit)}
      />
      <select
        className="select"
        value={unit}
        aria-label={unitName}
        onChange={(e) => onChange(value, e.target.value as QuantityUnit)}
      >
        {packName ? <option value="pack">{plural(packName, "2")}</option> : null}
        {units.map((u) => (
          <option key={u} value={u}>
            {unitLabel(u)}
          </option>
        ))}
      </select>
    </div>
  );
}

/** An amount and what it buys: "32.50 per bag" or "0.742 per lb". */
export function PriceInput({
  amount,
  per,
  onChange,
  dimension,
  packName,
  id,
  "aria-describedby": describedBy,
}: {
  amount: string;
  per: QuantityUnit;
  onChange: (amount: string, per: QuantityUnit) => void;
  dimension: Dimension;
  packName: string | null;
  id?: string;
  "aria-describedby"?: string;
}) {
  return (
    <div className="quantity-input">
      <span className="input-prefix" aria-hidden>
        $
      </span>
      <input
        id={id}
        aria-describedby={describedBy}
        className="input num"
        inputMode="decimal"
        autoComplete="off"
        placeholder="Optional"
        value={amount}
        onChange={(e) => onChange(e.target.value.replace(",", ".").replace("$", ""), per)}
      />
      <select
        className="select"
        value={per}
        aria-label="Price per"
        onChange={(e) => onChange(amount, e.target.value as QuantityUnit)}
      >
        {packName ? <option value="pack">per {packName}</option> : null}
        {DISPLAY_UNITS[dimension].map((u) => (
          <option key={u} value={u}>
            per {unitLabel(u)}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ---------- Display ---------- */

export function CategoryChip({ category }: { category: string }) {
  return (
    <span className="chip" style={{ "--chip": `var(--cat-${category})` } as CSSProperties}>
      {categoryLabel(category)}
    </span>
  );
}

export const catStyle = (category: string) =>
  ({ "--chip": `var(--cat-${category})` }) as CSSProperties;

/** A collapsible section whose open or closed state is remembered on this device. */
export function Section({
  id,
  title,
  aside,
  children,
}: {
  id: string;
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  const { layout, update } = useLayout();
  const collapsed = layout.item.collapsed[id] ?? false;
  const bodyId = useId();
  return (
    <section className="section">
      <button
        type="button"
        className="section-header"
        aria-expanded={!collapsed}
        aria-controls={bodyId}
        onClick={() =>
          update((l) => ({ ...l, item: { collapsed: { ...l.item.collapsed, [id]: !collapsed } } }))
        }
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        <h2>{title}</h2>
        {aside ? <span className="aside">{aside}</span> : null}
      </button>
      {collapsed ? null : (
        <div className="section-body" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      {children}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : "Something went wrong";
  return (
    <div className="form-error" role="alert">
      {message}
    </div>
  );
}
